<template>
  <div class="members-table">
    <table class="table table-responsive">
      <thead>
        <tr :class="`bg-${module}`">
          <th>Nom</th>
          <th>Prénom</th>
          <th>Emails</th>
          <th>Rôle</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="m in members"
            :key="m.id">
          <td :id="`n-${m.id}`">{{ m.name }}</td>
          <td :id="`f-${m.id}`">{{ m.firstName }}</td>
          <td :id="`e-${m.id}`">{{ m.emails }}</td>
          <td>
            <span v-if="!m.action || selectableRoles.length === 1">
              {{ m.roleName }}
            </span>
            <span v-else>
              <dropdown tag="span">
                <a class="dropdown-toggle" role="button">
                  {{ m.roleName }}
                </a>
                <template slot="dropdown">
                  <li v-for="r in roles"
                      v-if="r.selectable && m.role !== r.value"
                      :key="`${m.id}-role-${r.value}`">
                    <a role="button"
                       @click.stop="update(m.ref, r.value)">
                      {{ r.name }}
                    </a>
                  </li>
                </template>
              </dropdown>
            </span>
          </td>
          <td>
            <span v-if="m.action">
              <a role="button" @click="askForRemoval(m)">Supprimer</a>
            </span>
          </td>
        </tr>
      </tbody>
    </table>
    <div class="row" v-if="admin">
      <div class="col-xs-12">
        <h3>Ajouter un membre</h3>
      </div>
      <div class="col-xs-12 col-md-8 col-lg-6">
        <form class="form">
          <div class="form-group">
            <label for="email" class="control-label">Adresse e-mail</label>
            <input placeholder="Adresse e-mail de l'utilisateur"
                   id="email"
                   class="form-control"
                   v-model.trim="add.userEmail"
                   required >
          </div>
          <div class="form-group">
            <label for="role" class="control-label">Rôle</label>
            <select required
                    id="role"
                    v-model.trim="add.userRole"
                    autofocus
                    class="form-control"
                    placeholder="Rôle" >
              <option v-for="r in roles"
                      v-if="r.selectable"
                      :key="`add-role-${r.value}`"
                      :value="r.value">{{ r.name }}</option>
            </select>
          </div>
          <btn type="default" @click.stop="addMember">Ajouter</btn>
        </form>
      </div>
      <div class="col-xs-12 error-container">
        <collapse v-model="showAlert">
          <alert :type="add.alert.type">
            <strong>{{ add.alert.title }}</strong>
            <p v-html="add.alert.text"></p>
          </alert>
        </collapse>
      </div>
    </div>
    <modal v-model="removeModal"
           @hide="removeModalEnded"
           title="Confirmation"
           :append-to-body="nested"
           ok-text="Oui" cancel-text="Non"
           ok-type="danger" cancel-type="primary">
      <p v-if="removeModal">
        Êtes vous certain.e de vouloir retirer
        <strong>
          {{ remove.referent.firstName }} {{ remove.referent.name }}
        </strong> des membres de la surveillance ?
      </p>
    </modal>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'
import tools from '@/sui/tools/main'

const errors = {
  std: `Si le problème persiste, contactez-nous via le formulaire de contact.`,
  net: `Une erreur s'est produite lors de la connexion au serveur, merci de
vérifier votre connexion internet avant de réessayer. Si le problème persiste,
contactez-nous via le formulaire de contact.`
}

export default {
  name: 'members-table-2019',

  props: {
    roles: {
      type: Array,
      required: true
    },

    module: {
      type: String,
      required: true
    },

    surveillance: {
      type: Object,
      required: true
    },

    nested: {
      type: Boolean,
      default: false
    }
  },

  data: function () {
    return {
      add: {
        alert: { type: '', text: '', title: '' },
        userEmail: null,
        userRole: null
      },
      remove: {
        referent: null
      },
      removeModal: false
    }
  },

  computed: {
    ...mapGetters({
      userId: 'user/id'
    }),

    selectableRoles: function () {
      const result = []
      this.roles.forEach(
        r => {
          if (r.selectable) {
            result.push(r)
          }
        }
      )
      return result
    },

    admin: function () {
      return this.surveillance.isReferent(this.userId, this.module)
    },

    showAlert: function (t) {
      const { alert } = this.add
      return alert.text !== '' || alert.title !== ''
    },

    members: function () {
      const vm = this
      if (!this.surveillance.hasProtocol(this.module)) {
        return []
      }
      const result = []
      const members = this.surveillance.modules[this.module].members
      for (let j in members) {
        const r = members[j]
        const role = r.role
        const action = this.admin &&
                       !this.surveillance.isRegistrator(r.user.id) &&
                       this.userId !== r.user.id
        result.push({
          ref: r,
          id: r.user.id,
          name: r.user.name,
          firstName: r.user.firstName,
          emails: this.displayEmails(r.user.emails),
          action: action,
          role: role,
          roleName: this.getRoleName(role)
        })
      }

      return result.sort((first, second) => {
        const ref = vm.surveillance.registrator.id
        const me = this.userId
        if (first.id === ref) {
          return -1
        } else if (second.id === ref) {
          return 1
        }
        if (first.id === me) {
          return -1
        } else if (second.id === me) {
          return 1
        }
        const firstName = `${first.name} ${first.firstName}`
        const secondName = `${second.name} ${second.firstName}`
        return firstName.localeCompare(secondName)
      })
    }
  },

  methods: {
    setAlert: function (type, text, title) {
      this.add.alert.type = type
      this.add.alert.text = text
      this.add.alert.title = title
    },

    resetAlert: function () {
      this.add.alert.type = ''
      this.add.alert.text = ''
      this.add.alert.title = ''
    },

    setError: function (text, title) {
      this.setAlert('danger', text, title)
    },

    setWarning: function (text, title) {
      this.setAlert('warning', text, title)
    },

    setSuccess: function (text, title) {
      this.setAlert('info', text, title)
    },

    addMember: function () {
      this.resetAlert()
      const { userEmail, userRole } = this.add

      if (userEmail == null || !tools.isEmailValid(userEmail)) {
        this.setError(
          'Veuillez entrer une adresse e-mail valide.',
          'Adresse e-mail invalide !'
        )
        return
      }
      if (userRole == null) {
        this.setError(
          'Le rôle du nouveau membre doit être renseigné.',
          'Rôle manquant !'
        )
        return
      }
      const role = parseInt(userRole, 10)
      if (role < 0 || role > 2) {
        this.setError(
          'Veuillez entrer un rôle valide.',
          'Rôle invalide !'
        )
        return
      }

      let request = {
        s: this.surveillance.id,
        u: userEmail,
        r: role,
        t: this.module
      }
      const vm = this
      this.$store.dispatch(`surveillances/addMember2019`, request).then(
        (response) => {
          if (response.data.err == null) {
            switch (response.data.v) {
              case 0: // New User
                vm.setWarning(
                  `Un e-mail va lui être envoyé avec la procédure à suivre pour
ouvrir son compte. Une fois enregistré, vous pourrez l'ajouter comme membre
de la surveillance en utilisant à nouveau ce formulaire.`,
                  `${request.u} n'a pas pu être ajouté aux membres de
cette surveillance car aucun compte utilisateur enregistré n'utilise cette
adresse e-mail.`
                )
                break
              case 1: // Ghost User
                vm.$notify({
                  type: 'success',
                  title: `${request.u} a bien été ajouté.e en tant que
membre de la surveillance !`,
                  duration: 10000,
                  content: `Un e-mail va lui être envoyé contenant la procédure
à suivre pour activer son compte et ainsi pouvoir accéder à la surveillance.`
                })
                break
              case 2: // Valid User
                vm.$notify({
                  type: 'success',
                  duration: 10000,
                  content: `Un e-mail de confirmation va lui être envoyé.`,
                  title: `${request.u} a bien été ajouté.e en tant que membre
de la surveillance.`
                })
                break
            }
            vm.add.userEmail = null
            vm.add.userRole = null
          } else {
            vm.setError(
              `${errors.std}`,
              `Impossible d'ajouter ${request.u} en tant que membre de
la surveillance.`
            )
          }
        }).catch(
        function (e) {
          vm.setError(
            `${errors.net}`,
            `Impossible d'ajouter ${request.u} en tant que membre de
la surveillance.`
          )
        })
    },

    displayEmails (l) {
      return l.join(', ')
    },

    getRoleName (role) {
      const r = parseInt(role, 10)
      const v = this.roles.find(e => e.value === r)
      if (v != null) {
        return v.name
      }

      return 'Unknown Role'
    },

    getUserRole (u) {
      const members = this.surveillance.members[this.module]
      for (let o of members) {
        if (o.user.id === u) {
          return o.role
        }
      }
      return -1
    },

    update: function (r, role) {
      const u = r.user
      let instance = this
      if (role === r.role) {
        instance.$notify({
          type: 'info',
          title: 'Attention !',
          content: `Le rôle de ${u.firstName} ${u.name} n'a pas changé.`
        })
        return
      }

      let request = {
        s: this.surveillance.id,
        u: u.id,
        r: role,
        t: this.module
      }
      const roleName = this.getRoleName(role)
      this.$store.dispatch(`surveillances/updateMember2019`, request).then(
        (response) => {
          if (response.data.err == null) {
            instance.$notify({
              type: 'success',
              title: `Le rôle de ${u.firstName} ${u.name} a été modifié
avec succès !`,
              duration: 1000000,
              content: `${u.firstName} ${u.name} est maintenant ${roleName}
pour cette modalité.`
            })
          } else {
            instance.$notify({
              type: 'danger',
              title: `Impossible de modifier le rôle de ${u.firstName}
${u.name} !`,
              duration: 0,
              content: `${errors.std}`
            })
          }
        }).catch(function (e) {
        instance.$notify({
          type: 'danger',
          title: `Impossible de modifier le rôle de ${u.firstName}
${u.name} !`,
          duration: 0,
          content: `${errors.net}`
        })
      })
    },

    askForRemoval: function (r) {
      this.removeModal = true
      this.remove.referent = r
    },

    removeModalEnded: function (msg) {
      if (msg === 'ok') {
        this.removeMember(this.remove.referent)
      }
      this.remove.referent = null
      this.remove.type = null
    },

    removeMember (u) {
      let request = {
        s: this.surveillance.id,
        u: u.id,
        t: this.module
      }
      let instance = this
      this.$store.dispatch(`surveillances/removeMember2019`, request).then(
        (response) => {
          if (response.data.err == null) {
            instance.$notify({
              type: 'success',
              title: 'Succès !',
              content: `${u.firstName} ${u.name} a bien été retiré des membres
de la surveillance.`
            })
          } else {
            instance.$notify({
              type: 'danger',
              title: `Impossible de retirer ${u.firstName} ${u.name} des
membres de la surveillance !`,
              duration: 0,
              content: `${errors.std}`
            })
          }
        }).catch(function (e) {
        instance.$notify({
          type: 'danger',
          title: `Impossible de retirer ${u.firstName} ${u.name} des
membres de la surveillance !`,
          duration: 0,
          content: `${errors.net}`
        })
      })
    }

  }
}
</script>
<style scoped lang="less">
@import '~variables';

.members-table {
  color: @base-text;
  a:hover {
    cursor: pointer;
  }

  .control-label {
    margin-right: 1rem;
    font-weight: normal;
  }

  h3 {
    margin-top: 4rem;
  }

  .table {
    margin-bottom: 0rem;
  }

  tr {
    border-radius: 2px;
  }

  .error-container {
    margin-top: 2rem;
  }
}
</style>
